import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import * as userUrls from '../../../urls/api-users/admin';

const SERVICE_PHONE_NUMBER = '+18334674748';
const usersHost = userUrls.dispatchUsersHost || userUrls.usersHost;
const usersHasuraHost = userUrls.dispatchUsersHasuraHost || userUrls.hasuraHost;

const getDisplayName = info => {
  if (!info) {
    return 'Contact';
  }

  if (info.chatDisplayName) {
    return info.chatDisplayName;
  }

  const fallbackName = `${info.firstname || ''} ${info.lastname || ''}`.trim();
  return fallbackName || 'Contact';
};

const getUserRecordDisplayName = userRecord => {
  if (!userRecord) {
    return '';
  }

  return String(
    [userRecord.firstname || userRecord.first_name, userRecord.lastname || userRecord.last_name]
      .filter(Boolean)
      .join(' ') || userRecord.username || userRecord.email || ''
  ).trim();
};

const getInitials = name => {
  if (!name) {
    return 'C';
  }

  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
};

const isImageMedia = contentType => String(contentType || '').toLowerCase().startsWith('image/');
const isVideoMedia = contentType => String(contentType || '').toLowerCase().startsWith('video/');

const MessageMediaAttachment = ({ attachment }) => {
  const [blobUrl, setBlobUrl] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let objectUrl = '';

    if (!attachment || !attachment.proxy_path) {
      setLoadFailed(true);
      return undefined;
    }

    setLoadFailed(false);

    axios({
      method: 'get',
      url: `${usersHost}${attachment.proxy_path}`,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
      },
      responseType: 'blob',
    })
      .then(response => {
        if (!isMounted) {
          return;
        }

        objectUrl = window.URL.createObjectURL(response.data);
        setBlobUrl(objectUrl);
      })
      .catch(() => {
        if (isMounted) {
          setLoadFailed(true);
        }
      });

    return () => {
      isMounted = false;
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [attachment]);

  if (loadFailed) {
    return <div className="message-media-fallback">Attachment unavailable</div>;
  }

  if (!blobUrl) {
    return <div className="message-media-loading">Loading attachment...</div>;
  }

  if (isImageMedia(attachment.content_type)) {
    return (
      <a className="message-media-link" href={blobUrl} target="_blank" rel="noopener noreferrer">
        <img className="message-media-preview" src={blobUrl} alt={attachment.filename || 'Message attachment'} />
      </a>
    );
  }

  if (isVideoMedia(attachment.content_type)) {
    return (
      <video className="message-media-preview" controls preload="metadata">
        <source src={blobUrl} type={attachment.content_type || 'video/mp4'} />
      </video>
    );
  }

  return (
    <a className="message-media-file" href={blobUrl} download={attachment.filename || 'attachment'}>
      {attachment.filename || 'Download attachment'}
    </a>
  );
};

const MessageLogsContainer = ({ driverMessageRetrieved, messageRecipientInfo }) => {
  const containerRef = useRef(null);
  const isPinnedToBottomRef = useRef(true);
  const lastMessageSignatureRef = useRef('');
  const [senderNamesByAuth0Id, setSenderNamesByAuth0Id] = useState({});
  const recipientName = getDisplayName(messageRecipientInfo);
  const isClaimedOrderThread = messageRecipientInfo && messageRecipientInfo.chatMode === 'order-thread';
  const isPreClaimOrderThread = messageRecipientInfo && messageRecipientInfo.chatMode === 'preclaim-order-thread';
  const threadType = (messageRecipientInfo && messageRecipientInfo.chatThreadType) || 'claimed_order';
  const adminDisplayName = (messageRecipientInfo && messageRecipientInfo.chatAdminDisplayName) || 'Dispatch';
  const customerDisplayName = (messageRecipientInfo && messageRecipientInfo.chatCustomerDisplayName) || 'Customer';
  const driverDisplayName = (messageRecipientInfo && messageRecipientInfo.chatDriverDisplayName) || recipientName || 'Driver';

  useEffect(() => {
    const auth0Ids = Array.from(new Set(
      (driverMessageRetrieved || [])
        .map(log => String(log && log.sender_auth0_id ? log.sender_auth0_id : '').trim())
        .filter(Boolean)
    ));

    const missingAuth0Ids = auth0Ids.filter(auth0Id => !senderNamesByAuth0Id[auth0Id]);

    if (missingAuth0Ids.length === 0 || !usersHasuraHost) {
      return undefined;
    }

    let cancelled = false;

    axios({
      method: 'post',
      url: usersHasuraHost,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        'content-type': 'application/json',
      },
      data: {
        query: `query ResolveChatSenderNames($auth0Ids: [String!]!) {
          Users(where: { auth0_id: { _in: $auth0Ids } }) {
            auth0_id
            firstname
            lastname
            username
            email
          }
        }`,
        variables: {
          auth0Ids: missingAuth0Ids,
        },
      },
    })
      .then(response => {
        if (cancelled) {
          return;
        }

        const users = response && response.data && response.data.data && Array.isArray(response.data.data.Users)
          ? response.data.data.Users
          : [];

        if (users.length === 0) {
          return;
        }

        const resolvedNames = users.reduce((lookup, userRecord) => {
          const auth0Id = String(userRecord && userRecord.auth0_id ? userRecord.auth0_id : '').trim();
          const displayName = getUserRecordDisplayName(userRecord);

          if (auth0Id && displayName) {
            lookup[auth0Id] = displayName;
          }

          return lookup;
        }, {});

        if (Object.keys(resolvedNames).length === 0) {
          return;
        }

        setSenderNamesByAuth0Id(currentValue => ({
          ...currentValue,
          ...resolvedNames,
        }));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [driverMessageRetrieved, senderNamesByAuth0Id]);

  const resolveSenderDisplayName = log => {
    const senderAuth0Id = String(log && log.sender_auth0_id ? log.sender_auth0_id : '').trim();
    const senderRole = String((log && log.sender_role) || '').toLowerCase();
    const resolvedSenderName = senderAuth0Id ? senderNamesByAuth0Id[senderAuth0Id] : '';

    if (messageRecipientInfo && messageRecipientInfo.chatParticipantAuth0Id && senderAuth0Id === messageRecipientInfo.chatParticipantAuth0Id) {
      return customerDisplayName;
    }

    if (messageRecipientInfo && messageRecipientInfo.chatDriverAuth0Id && senderAuth0Id === messageRecipientInfo.chatDriverAuth0Id) {
      return resolvedSenderName || driverDisplayName;
    }

    if (senderRole === 'dispatch') {
      return resolvedSenderName || adminDisplayName;
    }

    if (senderRole === 'customer') {
      return resolvedSenderName || customerDisplayName;
    }

    if (senderRole === 'driver') {
      return resolvedSenderName || driverDisplayName;
    }

    return resolvedSenderName || '';
  };

  useEffect(() => {
    // Only auto-scroll to the bottom when new messages actually arrive AND the user is already
    // pinned to the bottom. This prevents polling re-renders (which hand us a fresh array every
    // few seconds) from yanking the view down while the user is scrolled up reading history.
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const messages = driverMessageRetrieved || [];
    const signature = `${messages.length}|${messages.length ? (messages[messages.length - 1].message_id || messages[messages.length - 1].sid || '') : ''}`;
    const hasNewMessages = signature !== lastMessageSignatureRef.current;
    const isFirstRender = lastMessageSignatureRef.current === '';
    lastMessageSignatureRef.current = signature;

    if ((isFirstRender || hasNewMessages) && isPinnedToBottomRef.current) {
      container.scrollTop = container.scrollHeight;
    }
  }, [driverMessageRetrieved]);

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    isPinnedToBottomRef.current = distanceFromBottom <= 40;
  };

  return (
    <div className="message-logs-container" ref={containerRef} onScroll={handleScroll}>
      {driverMessageRetrieved.map(log => {
        const hasExplicitDirection = typeof log.direction === 'string';
        const isReceived = hasExplicitDirection ? log.direction !== 'sent' : log.to === SERVICE_PHONE_NUMBER;
        const senderName = isReceived ? recipientName : adminDisplayName;
        const isCustomerDriverThread = isClaimedOrderThread && messageRecipientInfo && messageRecipientInfo.chatThreadType === 'customer_driver';
        const isCustomerDispatchThread = isClaimedOrderThread && threadType === 'customer_dispatch';
        const resolvedSenderDisplayName = resolveSenderDisplayName(log);
        const normalizedSenderName = (isClaimedOrderThread || isPreClaimOrderThread)
          ? (() => {
            if (resolvedSenderDisplayName) {
              return resolvedSenderDisplayName;
            }

            if (isCustomerDispatchThread && hasExplicitDirection) {
              return isReceived ? adminDisplayName : customerDisplayName;
            }

            if (isCustomerDriverThread) {
              return driverDisplayName;
            }

            return hasExplicitDirection
              ? (isReceived ? driverDisplayName : adminDisplayName)
              : senderName;
          })()
          : senderName;
        const initials = getInitials(normalizedSenderName);
        const logKey = log.message_id || log.sid;
        const logTime = log.created_at || log.time;

        return (
          <div className={`message-log ${isReceived ? 'received' : 'sent'}`} key={logKey}>
            <div className={`message-log-avatar ${isReceived ? 'received' : 'sent'}`}>{initials}</div>
            <div className="message-log-content">
              <div className="message-log-author">{normalizedSenderName}</div>
              {Array.isArray(log.media) && log.media.length > 0 ? (
                <div className="message-media-group">
                  {log.media.map(attachment => (
                    <MessageMediaAttachment key={attachment.sid || attachment.proxy_path} attachment={attachment} />
                  ))}
                </div>
              ) : null}
              {log.body ? <div className={`${isReceived ? 'received-message' : 'sent-message'}`}>{log.body}</div> : null}
              <div className="message-info">
                <div className="message-date">{moment(logTime).format('MMM D, h:mm A')}</div>
                {log.status ? <div className={`${log.status === 'undelivered' ? 'message-status-alert' : 'message-status'}`}>{log.status}</div> : null}
              </div>
              {log.status === 'undelivered' && (log.error_code || log.error_message) ? (
                <div className="message-status-alert" style={{ marginTop: '4px' }}>
                  {[log.error_code ? `Code ${log.error_code}` : null, log.error_message || null].filter(Boolean).join(': ')}
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MessageLogsContainer;
